import { useEffect, useMemo, useState, useCallback } from 'react'
import { DndProvider, useDrag, useDrop } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import {
  apiTacticsLoad, apiTacticsSave, apiTacticsActivate,
} from '../server/ServiceCall'
import { AuthState } from '../store/auth'

interface Player {
  PLAYER_CODE:   number
  PLAYER_NAME:   string
  POSITION:      string
  SUB_POSITIONS: string
  NATIONALITY:   string
  AGE:           number
  OVERALL:       number
  STAMINA:       number
  PAC?: number; SHO?: number; PAS?: number; DRI?: number; DEF?: number; PHY?: number
  REF?: number; HAN?: number; KIK?: number; POS_GK?: number
}

interface Slot {
  name: string
  pos:  string
}

interface FormationRow {
  FORMATION_CODE: number
  FORMATION_NAME: string
  SLOTS_JSON:     string
  COUNTER_JSON:   string
  DESCRIPTION:    string | null
}

interface TacticsRow {
  SLOT_NO:          number
  SLOT_NAME:        string
  FORMATION_CODE:   number
  STARTING_11_JSON: string
  BENCH_JSON:       string
  IS_VALID:         number
  IS_ACTIVE:        number
}

interface UnavailRow {
  PLAYER_CODE: number
  STATUS_TYPE: number   // 1=부상, 2=출장정지, 3=이적협상
  REASON_TXT:  string
  RETURN_DATE: string | null
}

const ITEM_TYPE = 'PLAYER'
const STATUS_LABEL: Record<number, string> = { 1: '부상', 2: '출장정지', 3: '이적협상' }

// 진형별 슬롯 → [row, col] 좌표 (8 컬럼 × 5 행 그리드)
// row 1 = GK, row 2 = 수비, row 3 = 미드, row 4~5 = 공격
const FORMATION_LAYOUT: Record<number, Record<string, [number, number]>> = {
  1: { // 4-4-2
    GK:[1,4], LB:[2,1], CB1:[2,3], CB2:[2,5], RB:[2,7],
    LM:[3,1], CM1:[3,3], CM2:[3,5], RM:[3,7], ST1:[4,3], ST2:[4,5],
  },
  2: { // 4-3-3
    GK:[1,4], LB:[2,1], CB1:[2,3], CB2:[2,5], RB:[2,7],
    CM1:[3,2], DM:[3,4], CM2:[3,6],
    LW:[4,1], ST:[4,4], RW:[4,7],
  },
  3: { // 4-2-3-1
    GK:[1,4], LB:[2,1], CB1:[2,3], CB2:[2,5], RB:[2,7],
    DM1:[3,3], DM2:[3,5],
    LW:[4,2], AM:[4,4], RW:[4,6], ST:[5,4],
  },
  4: { // 3-5-2
    GK:[1,4], CB1:[2,2], CB2:[2,4], CB3:[2,6],
    LM:[3,1], DM:[3,3], CM1:[3,4], CM2:[3,5], RM:[3,7],
    ST1:[4,3], ST2:[4,5],
  },
  5: { // 3-4-3
    GK:[1,4], CB1:[2,2], CB2:[2,4], CB3:[2,6],
    LM:[3,1], CM1:[3,3], CM2:[3,5], RM:[3,7],
    LW:[4,1], ST:[4,4], RW:[4,7],
  },
  6: { // 5-3-2
    GK:[1,4], LB:[2,1], CB1:[2,2], CB2:[2,4], CB3:[2,6], RB:[2,7],
    DM:[3,3], CM1:[3,4], CM2:[3,5],
    ST1:[4,3], ST2:[4,5],
  },
  7: { // 4-1-4-1
    GK:[1,4], LB:[2,1], CB1:[2,3], CB2:[2,5], RB:[2,7],
    DM:[3,4],
    LM:[4,1], CM1:[4,3], CM2:[4,5], RM:[4,7], ST:[5,4],
  },
  8: { // 4-5-1
    GK:[1,4], LB:[2,1], CB1:[2,3], CB2:[2,5], RB:[2,7],
    LM:[3,1], CM1:[3,3], CM2:[3,4], CM3:[3,5], RM:[3,7], ST:[4,4],
  },
  9: { // 4-3-1-2 다이아몬드
    GK:[1,4], LB:[2,1], CB1:[2,3], CB2:[2,5], RB:[2,7],
    DM:[3,4], CM1:[4,2], CM2:[4,6], AM:[4,4],
    ST1:[5,3], ST2:[5,5],
  },
  10: { // 3-4-2-1
    GK:[1,4], CB1:[2,2], CB2:[2,4], CB3:[2,6],
    LM:[3,1], CM1:[3,3], CM2:[3,5], RM:[3,7],
    AM1:[4,3], AM2:[4,5], ST:[5,4],
  },
  11: { // 4-4-2 다이아몬드 (4 미드 다이아몬드 + 투톱)
    GK:[1,4], LB:[2,1], CB1:[2,3], CB2:[2,5], RB:[2,7],
    DM:[3,4], CM1:[4,2], CM2:[4,6], AM:[4,4],
    ST1:[5,3], ST2:[5,5],
  },
}

interface Props {
  auth:     AuthState
  clubCode: number
}

type Location =
  | { kind: 'pool' }
  | { kind: 'slot', slotName: string }
  | { kind: 'bench', idx: number }

interface DragItem {
  playerCode: number
  origin:     Location
}

interface SlotState {
  slotNo:         number
  slotName:       string
  formationCode:  number
  starting:       Record<string, number | null>
  bench:          (number | null)[]
  isValid:        boolean
  isActive:       boolean
}

const EMPTY_BENCH: (number | null)[] = [null, null, null, null, null, null, null]

export default function Tactics({ auth, clubCode }: Props) {
  const [formations, setFormations] = useState<FormationRow[]>([])
  const [pool, setPool]             = useState<Player[]>([])
  const [byCode, setByCode]         = useState<Record<number, Player>>({})
  const [unavail, setUnavail]       = useState<Record<number, UnavailRow>>({})
  const [slotsByCode, setSlotsByCode] = useState<Record<number, Slot[]>>({})
  const [activeSlotNo, setActiveSlotNo] = useState<number>(1)
  const [tacticsBySlot, setTacticsBySlot] = useState<Record<number, SlotState>>({})
  const [editSlotNo, setEditSlotNo] = useState<number>(1)
  const [loading, setLoading]       = useState(true)
  const [msg, setMsg]               = useState('')
  const [selected, setSelected]     = useState<Player | null>(null)

  // 진형 코드 → slots 배열 인덱스화
  const buildSlotsByCode = useCallback((masters: FormationRow[]) => {
    const idx: Record<number, Slot[]> = {}
    masters.forEach(m => {
      try { idx[m.FORMATION_CODE] = JSON.parse(m.SLOTS_JSON) as Slot[] } catch {}
    })
    return idx
  }, [])

  const reload = useCallback(async () => {
    setLoading(true); setMsg('')
    const r = await apiTacticsLoad(auth.token, clubCode)
    if (r.resultCode !== '00') { setMsg(r.resultMessage || '조회 실패'); setLoading(false); return }
    try {
      const obj = JSON.parse(r.resultData) as {
        tactics:     TacticsRow[],
        formations:  FormationRow[],
        players:     Player[],
        unavailable: UnavailRow[],
      }
      const masters = obj.formations ?? []
      setFormations(masters)
      setSlotsByCode(buildSlotsByCode(masters))

      const players = obj.players ?? []
      setPool(players)
      const codeIdx: Record<number, Player> = {}
      players.forEach(p => { codeIdx[p.PLAYER_CODE] = p })
      setByCode(codeIdx)

      const unavailIdx: Record<number, UnavailRow> = {}
      ;(obj.unavailable ?? []).forEach(u => { unavailIdx[u.PLAYER_CODE] = u })
      setUnavail(unavailIdx)

      // 슬롯 3개 상태 빌드
      const next: Record<number, SlotState> = {}
      let active = 1
      ;(obj.tactics ?? []).forEach(t => {
        const slotDef = (() => { try { return JSON.parse(masters.find(f => f.FORMATION_CODE === t.FORMATION_CODE)?.SLOTS_JSON ?? '[]') as Slot[] } catch { return [] } })()
        const startingMap: Record<string, number | null> = {}
        slotDef.forEach(s => { startingMap[s.name] = null })
        try {
          const st = JSON.parse(t.STARTING_11_JSON || '{}') as Record<string, number>
          Object.keys(st).forEach(k => { startingMap[k] = st[k] })
        } catch {}
        const bench: (number | null)[] = [...EMPTY_BENCH]
        try {
          const bn = JSON.parse(t.BENCH_JSON || '[]') as number[]
          bn.forEach((c, i) => { if (i < 7) bench[i] = c })
        } catch {}
        next[t.SLOT_NO] = {
          slotNo:        t.SLOT_NO,
          slotName:      t.SLOT_NAME || `전술${t.SLOT_NO}`,
          formationCode: t.FORMATION_CODE || 1,
          starting:      startingMap,
          bench,
          isValid:       t.IS_VALID === 1,
          isActive:      t.IS_ACTIVE === 1,
        }
        if (t.IS_ACTIVE === 1) active = t.SLOT_NO
      })
      setTacticsBySlot(next)
      setActiveSlotNo(active)
      setEditSlotNo(prev => next[prev] ? prev : active)
    } catch (e) { setMsg('응답 파싱 실패') }
    finally { setLoading(false) }
  }, [auth.token, clubCode, buildSlotsByCode])

  useEffect(() => { reload() }, [reload])

  // 현재 편집 중 슬롯 상태
  const cur = tacticsBySlot[editSlotNo]
  const slots = useMemo(() => {
    if (!cur) return []
    return slotsByCode[cur.formationCode] ?? []
  }, [cur, slotsByCode])

  const usedCodes = useMemo(() => {
    const s = new Set<number>()
    if (cur) {
      Object.values(cur.starting).forEach(v => { if (v) s.add(v) })
      cur.bench.forEach(v => { if (v) s.add(v) })
    }
    return s
  }, [cur])

  const poolView = useMemo(() => pool.filter(p => !usedCodes.has(p.PLAYER_CODE)), [pool, usedCodes])

  // ── 슬롯 상태 갱신 헬퍼 ─────────────────────────────────
  function patchCur(patch: Partial<SlotState>) {
    setTacticsBySlot(prev => {
      const c = prev[editSlotNo]
      if (!c) return prev
      return { ...prev, [editSlotNo]: { ...c, ...patch } }
    })
  }

  // ── 진형 변경 ──────────────────────────────────────────
  function changeFormation(fc: number) {
    if (!cur) return
    const newSlots = slotsByCode[fc] ?? []
    const empty: Record<string, number | null> = {}
    newSlots.forEach(s => { empty[s.name] = null })
    patchCur({ formationCode: fc, starting: empty, bench: [...EMPTY_BENCH], isValid: false })
  }

  // ── 이동 (드래그앤드롭) ────────────────────────────────
  function moveTo(item: DragItem, dest: Location) {
    if (!cur) return
    const startN = { ...cur.starting }
    let benchN = [...cur.bench]

    const removeFrom = (loc: Location) => {
      if (loc.kind === 'slot')  startN[loc.slotName] = null
      if (loc.kind === 'bench') benchN[loc.idx]      = null
    }
    const occupant = (() => {
      if (dest.kind === 'slot')  return startN[dest.slotName] ?? null
      if (dest.kind === 'bench') return benchN[dest.idx] ?? null
      return null
    })()

    removeFrom(item.origin)
    if (dest.kind === 'slot')  startN[dest.slotName] = item.playerCode
    if (dest.kind === 'bench') benchN[dest.idx]      = item.playerCode

    if (occupant) {
      if (item.origin.kind === 'slot')       startN[item.origin.slotName] = occupant
      else if (item.origin.kind === 'bench') benchN[item.origin.idx]      = occupant
      // origin === pool → occupant는 풀 자연 복귀
    }

    patchCur({ starting: startN, bench: benchN })
  }

  // ── 초기화 ─────────────────────────────────────────────
  function handleReset() {
    if (!cur) return
    if (!window.confirm(`${cur.slotName} 의 모든 선수 배치를 초기화합니다. 진행할까요?`)) return
    const newSlots = slotsByCode[cur.formationCode] ?? []
    const empty: Record<string, number | null> = {}
    newSlots.forEach(s => { empty[s.name] = null })
    patchCur({ starting: empty, bench: [...EMPTY_BENCH] })
    setMsg(`${cur.slotName} 초기화 완료 (저장 누르면 반영)`)
  }

  // ── 자동 배치 ──────────────────────────────────────────
  function handleAutoFill() {
    if (!cur) return
    const stMap: Record<string, number | null> = {}
    slots.forEach(s => { stMap[s.name] = null })
    const used = new Set<number>()
    const available = pool.filter(p => !unavail[p.PLAYER_CODE])
    const remaining = [...available].sort((a, b) => b.OVERALL - a.OVERALL)
    for (const slot of slots) {
      const cand = remaining.find(p =>
        !used.has(p.PLAYER_CODE) &&
        (p.POSITION === slot.pos || p.SUB_POSITIONS.split(',').map(s => s.trim()).includes(slot.pos))
      )
      if (cand) { stMap[slot.name] = cand.PLAYER_CODE; used.add(cand.PLAYER_CODE) }
    }
    for (const slot of slots) {
      if (stMap[slot.name] != null) continue
      const cand = remaining.find(p => !used.has(p.PLAYER_CODE))
      if (cand) { stMap[slot.name] = cand.PLAYER_CODE; used.add(cand.PLAYER_CODE) }
    }
    const benchArr: (number | null)[] = [...EMPTY_BENCH]
    let bi = 0
    for (const p of remaining) {
      if (!used.has(p.PLAYER_CODE) && bi < 7) { benchArr[bi] = p.PLAYER_CODE; bi++ }
    }
    patchCur({ starting: stMap, bench: benchArr })
    setMsg('자동 배치 완료 (저장 누르면 반영)')
  }

  // ── 저장 ───────────────────────────────────────────────
  async function handleSave() {
    if (!cur) return
    setMsg('저장 중…')
    const stMap: Record<string, number> = {}
    slots.forEach(s => { const v = cur.starting[s.name]; if (v) stMap[s.name] = v })
    const bnArr = cur.bench.filter((v): v is number => !!v)
    const r = await apiTacticsSave(auth.token, clubCode, cur.slotNo, cur.slotName,
      cur.formationCode, JSON.stringify(stMap), JSON.stringify(bnArr))
    setMsg(`${r.resultCode === '00' ? '✅' : '⚠️'} ${r.resultMessage}`)
    if (r.resultCode === '00') reload()
  }

  // ── 활성화 ─────────────────────────────────────────────
  async function handleActivate() {
    if (!cur) return
    if (!cur.isValid) {
      setMsg('⚠️ 검증 실패한 슬롯은 활성화할 수 없습니다. 먼저 문제를 해결하세요.')
      return
    }
    const r = await apiTacticsActivate(auth.token, clubCode, cur.slotNo)
    setMsg(`${r.resultCode === '00' ? '⭐' : '⚠️'} ${r.resultMessage}`)
    if (r.resultCode === '00') reload()
  }

  // ── 슬롯명 인라인 편집 ─────────────────────────────────
  function handleNameChange(v: string) {
    patchCur({ slotName: v })
  }

  // ── 검증 결과 계산 (현재 편집 슬롯) ────────────────────
  const validation = useMemo(() => {
    if (!cur || slots.length === 0) return null
    let placed = 0, mismatch = 0, unavailCnt = 0
    const issues: string[] = []
    for (const slot of slots) {
      const code = cur.starting[slot.name]
      if (!code) { issues.push(`${slot.name}(${slot.pos}) 미배치`); continue }
      placed++
      const p = byCode[code]
      if (!p) continue
      const subs = p.SUB_POSITIONS.split(',').map(s => s.trim())
      if (p.POSITION !== slot.pos && !subs.includes(slot.pos)) {
        mismatch++
        issues.push(`${slot.name}(${slot.pos}) ← ${p.PLAYER_NAME}(${p.POSITION}) 부적합`)
      }
      if (unavail[code]) {
        unavailCnt++
        const u = unavail[code]
        issues.push(`${slot.name} ← ${p.PLAYER_NAME} ${STATUS_LABEL[u.STATUS_TYPE]}: ${u.REASON_TXT}`)
      }
    }
    const ok = placed === 11 && mismatch === 0 && unavailCnt === 0
    return { placed, total: 11, mismatch, unavailCnt, ok, issues }
  }, [cur, slots, byCode, unavail])

  if (loading) return <div className="page-pad">전술 불러오는 중…</div>
  if (!cur) return <div className="page-pad">슬롯 데이터 없음</div>

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="page-pad tactics-page">
        {/* 슬롯 탭 */}
        <div className="tactics-slot-tabs">
          {[1, 2, 3].map(n => {
            const t = tacticsBySlot[n]
            const isActive  = t?.isActive
            const isCurrent = editSlotNo === n
            return (
              <button
                key={n}
                className={`slot-tab${isCurrent ? ' current' : ''}${isActive ? ' active' : ''}`}
                onClick={() => setEditSlotNo(n)}
              >
                {isActive && <span className="active-mark">⭐</span>}
                {t?.slotName ?? `전술${n}`}
                {!t?.isValid && t && <span className="warn-mark">⚠️</span>}
              </button>
            )
          })}
        </div>

        <div className="tactics-head">
          <input
            className="slot-name-input"
            value={cur.slotName}
            maxLength={32}
            onChange={e => handleNameChange(e.target.value)}
          />
          <select value={cur.formationCode} onChange={e => changeFormation(Number(e.target.value))}>
            {formations.map(f => (
              <option key={f.FORMATION_CODE} value={f.FORMATION_CODE}>{f.FORMATION_NAME}</option>
            ))}
          </select>
          <button onClick={handleAutoFill}>자동 배치</button>
          <button onClick={handleReset}>초기화</button>
          <button className="primary" onClick={handleSave}>저장</button>
          <button
            className={cur.isActive ? 'active-on' : 'active-off'}
            onClick={handleActivate}
            disabled={cur.isActive}
            title={cur.isActive ? '이 슬롯이 활성화되어 있습니다' : '이 슬롯을 라이브 매치에 사용'}
          >
            {cur.isActive ? '⭐ 활성중' : '활성화'}
          </button>
          {msg && <span className="action-msg">{msg}</span>}
        </div>

        {/* 검증 패널 */}
        {validation && (
          <div className={`validation-panel ${validation.ok ? 'ok' : 'warn'}`}>
            <div className="v-summary">
              <span>배치: {validation.placed}/11</span>
              <span>슬롯불일치: {validation.mismatch}</span>
              <span>사용불가: {validation.unavailCnt}</span>
              <span className="v-status">{validation.ok ? '✅ 경기 가능' : '⚠️ 검증 실패 — 활성화 불가'}</span>
            </div>
            {!validation.ok && validation.issues.length > 0 && (
              <ul className="v-issues">
                {validation.issues.slice(0, 6).map((it, i) => <li key={i}>{it}</li>)}
                {validation.issues.length > 6 && <li>외 {validation.issues.length - 6}건…</li>}
              </ul>
            )}
          </div>
        )}

        <div className="tactics-body">
          {/* 좌: 사용 가능 풀 */}
          <div className="tactics-pool">
            <h3>선수 풀 ({poolView.length}명)</h3>
            <div className="pool-list">
              {poolView.map(p => (
                <PlayerCard
                  key={p.PLAYER_CODE}
                  player={p}
                  origin={{ kind: 'pool' }}
                  unavail={unavail[p.PLAYER_CODE]}
                  selected={selected?.PLAYER_CODE === p.PLAYER_CODE}
                  onSelect={setSelected}
                />
              ))}
            </div>
          </div>

          {/* 중: 필드 11 슬롯 */}
          <div className="tactics-field">
            <div className="field-grid">
              {slots.map(s => {
                const layout = FORMATION_LAYOUT[cur.formationCode]?.[s.name]
                const cellStyle = layout
                  ? { gridRow: layout[0], gridColumn: layout[1] }
                  : undefined
                return (
                  <SlotCell
                    key={s.name}
                    slot={s}
                    player={byCode[cur.starting[s.name] ?? -1] ?? null}
                    unavail={unavail[cur.starting[s.name] ?? -1] ?? null}
                    selectedCode={selected?.PLAYER_CODE ?? null}
                    onSelect={setSelected}
                    onDrop={(item) => moveTo(item, { kind: 'slot', slotName: s.name })}
                    style={cellStyle}
                  />
                )
              })}
            </div>
          </div>

          {/* 우: 선수 상세 */}
          <div className="tactics-detail">
            <h3>선수 상세</h3>
            <PlayerDetailPanel player={selected} unavail={selected ? unavail[selected.PLAYER_CODE] : null} />
          </div>
        </div>

        {/* 하: 벤치 7 */}
        <div className="tactics-bench">
          <h3>벤치 ({cur.bench.filter(b => b).length}/7)</h3>
          <div className="bench-list">
            {cur.bench.map((code, i) => (
              <BenchCell
                key={i}
                idx={i}
                player={code ? byCode[code] : null}
                unavail={code ? unavail[code] ?? null : null}
                selectedCode={selected?.PLAYER_CODE ?? null}
                onSelect={setSelected}
                onDrop={(item) => moveTo(item, { kind: 'bench', idx: i })}
              />
            ))}
          </div>
        </div>
      </div>
    </DndProvider>
  )
}

// ─── 드래그 가능 선수 카드 ──────────────────────────────
function PlayerCard({ player, origin, selected, onSelect, unavail }: {
  player: Player, origin: Location, selected?: boolean,
  onSelect?: (p: Player) => void, unavail?: UnavailRow | null,
}) {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: ITEM_TYPE,
    item: { playerCode: player.PLAYER_CODE, origin } as DragItem,
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  }), [player.PLAYER_CODE, origin])

  const cls = `player-card${isDragging ? ' dragging' : ''}${selected ? ' selected' : ''}${unavail ? ' unavail' : ''} pos-${player.POSITION}`
  const sta = player.STAMINA ?? 100
  const staCls = sta < 40 ? 'sta low' : sta < 60 ? 'sta mid' : 'sta'
  return (
    <div ref={drag} className={cls} onClick={() => onSelect?.(player)}>
      {unavail && <span className="unavail-mark" title={`${STATUS_LABEL[unavail.STATUS_TYPE]}: ${unavail.REASON_TXT}`}>⛔</span>}
      <div className="pc-line1">
        <span className="pc-pos">{player.POSITION}</span>
        <span className="pc-ovr">{player.OVERALL}</span>
      </div>
      <div className="pc-name">{player.PLAYER_NAME}</div>
      <div className={staCls}><div className="sta-bar" style={{ width: `${sta}%` }}></div><span className="sta-num">{sta}</span></div>
    </div>
  )
}

// ─── 슬롯 셀 ────────────────────────────────────────────
function SlotCell({ slot, player, onDrop, selectedCode, onSelect, unavail, style }: {
  slot: Slot, player: Player | null, onDrop: (item: DragItem) => void,
  selectedCode: number | null, onSelect: (p: Player) => void,
  unavail?: UnavailRow | null,
  style?: React.CSSProperties,
}) {
  const [{ isOver, canDrop }, drop] = useDrop(() => ({
    accept: ITEM_TYPE,
    drop: (item: DragItem) => onDrop(item),
    collect: (monitor) => ({ isOver: monitor.isOver(), canDrop: monitor.canDrop() }),
  }), [onDrop])

  const valid = player ? (player.POSITION === slot.pos || player.SUB_POSITIONS.split(',').map(s => s.trim()).includes(slot.pos)) : true
  const issueClass = !player ? '' : (!valid ? ' mismatch' : (unavail ? ' has-unavail' : ''))

  return (
    <div ref={drop} className={`slot-cell${isOver ? ' over' : ''}${canDrop ? ' drop' : ''}${issueClass}`} style={style}>
      <div className="slot-label">{slot.name} <em>({slot.pos})</em></div>
      {player ? (
        <PlayerCard
          player={player}
          origin={{ kind: 'slot', slotName: slot.name }}
          selected={selectedCode === player.PLAYER_CODE}
          onSelect={onSelect}
          unavail={unavail}
        />
      ) : (
        <div className="slot-empty">비어있음</div>
      )}
    </div>
  )
}

// ─── 벤치 셀 ────────────────────────────────────────────
function BenchCell({ idx, player, onDrop, selectedCode, onSelect, unavail }: {
  idx: number, player: Player | null, onDrop: (item: DragItem) => void,
  selectedCode: number | null, onSelect: (p: Player) => void,
  unavail?: UnavailRow | null,
}) {
  const [{ isOver }, drop] = useDrop(() => ({
    accept: ITEM_TYPE,
    drop: (item: DragItem) => onDrop(item),
    collect: (monitor) => ({ isOver: monitor.isOver() }),
  }), [onDrop])

  return (
    <div ref={drop} className={`bench-cell${isOver ? ' over' : ''}`}>
      {player ? (
        <PlayerCard
          player={player}
          origin={{ kind: 'bench', idx }}
          selected={selectedCode === player.PLAYER_CODE}
          onSelect={onSelect}
          unavail={unavail}
        />
      ) : (
        <div className="bench-empty">B{idx + 1}</div>
      )}
    </div>
  )
}

// ─── 선수 상세 패널 ─────────────────────────────────────
function PlayerDetailPanel({ player, unavail }: { player: Player | null, unavail: UnavailRow | null | undefined }) {
  if (!player) {
    return <div className="detail-empty">선수를 클릭하면<br />상세 정보가 표시됩니다.</div>
  }
  const isGK = player.POSITION === 'GK'
  const subs = (player.SUB_POSITIONS || '').split(',').map(s => s.trim()).filter(Boolean)
  return (
    <div className="detail-body">
      <div className="detail-head">
        <span className={`detail-pos pos-${player.POSITION}`}>{player.POSITION}</span>
        <span className="detail-ovr">{player.OVERALL}</span>
      </div>
      <div className="detail-name">{player.PLAYER_NAME}</div>
      <div className="detail-meta">
        <span>{player.NATIONALITY}</span> · <span>만 {player.AGE}세</span> · <span>체력 {player.STAMINA ?? 100}</span>
      </div>
      {unavail && (
        <div className="detail-unavail">
          ⛔ <strong>{STATUS_LABEL[unavail.STATUS_TYPE]}</strong> — {unavail.REASON_TXT}
          {unavail.RETURN_DATE && <div className="detail-return">복귀 예정: {unavail.RETURN_DATE.slice(0, 10)}</div>}
        </div>
      )}
      {subs.length > 0 && (
        <div className="detail-subs">부포지션: {subs.join(' · ')}</div>
      )}
      <h4 className="detail-section">{isGK ? '골키퍼 능력치' : '필드 능력치'}</h4>
      <ul className="detail-stats">
        {isGK ? (
          <>
            <li><label>반응</label><meter min={0} max={99} value={player.REF || 0} /><span>{player.REF}</span></li>
            <li><label>핸들링</label><meter min={0} max={99} value={player.HAN || 0} /><span>{player.HAN}</span></li>
            <li><label>킥</label><meter min={0} max={99} value={player.KIK || 0} /><span>{player.KIK}</span></li>
            <li><label>포지셔닝</label><meter min={0} max={99} value={player.POS_GK || 0} /><span>{player.POS_GK}</span></li>
          </>
        ) : (
          <>
            <li><label>속도</label><meter min={0} max={99} value={player.PAC || 0} /><span>{player.PAC}</span></li>
            <li><label>슈팅</label><meter min={0} max={99} value={player.SHO || 0} /><span>{player.SHO}</span></li>
            <li><label>패스</label><meter min={0} max={99} value={player.PAS || 0} /><span>{player.PAS}</span></li>
            <li><label>드리블</label><meter min={0} max={99} value={player.DRI || 0} /><span>{player.DRI}</span></li>
            <li><label>수비</label><meter min={0} max={99} value={player.DEF || 0} /><span>{player.DEF}</span></li>
            <li><label>피지컬</label><meter min={0} max={99} value={player.PHY || 0} /><span>{player.PHY}</span></li>
          </>
        )}
      </ul>
    </div>
  )
}
